<template>
  <div>
    <HeaderNavbar title="Accounts" :breadcrumbs="listBreadcrumbs">
      <template #right-side>
        <div class="d-flex gap-3">
          <HeaderNavbarSearch v-model="searchQuery"></HeaderNavbarSearch>
          <RouterLink
            v-if="isAdmin"
            :to="{ name: 'settings.accounts.detail', params: { id: '+' } }"
            class="btn btn-primary"
            data-testid="accounts-create-button">
            {{ $t('account.create.button') }}
            <IconUserPlus :size="16" class="ms-2"></IconUserPlus>
          </RouterLink>
        </div>
      </template>
    </HeaderNavbar>

    <RemoteListView
      endpoint="/v1/account/accounts"
      :search-query="searchQuery"
      class="container-lead"
      autopilot-key="account_list"
      :autopilot-description="accountListDescription"
      :autopilot-transformer="accountListTransformer"
      :autopilot-link="accountListLink"
      :autopilot-filters="accountListFilters"
      @after-fetch="routeReady">
      <template #loading>
        <div class="row">
          <div class="col-md-4" v-for="i in new Array(6)" :key="i">
            <div class="card mb-3">
              <div class="card-body p-4">
                <h3 class="card-title mb-1">
                  <div class="placeholder col-7" />
                </h3>
                <div>
                  <div class="placeholder col-3" />
                  <br />
                  <div class="placeholder col-4" />
                  <br />
                  <div class="placeholder col-1" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #collection="{ collection: accounts }">
        <div class="row">
          <div class="col-md-4" v-for="(account, index) in accounts" :key="account.id">
            <div
              class="animate__animated animate__fadeInUp animate__faster card selectable-card mb-3"
              :style="{ animationDelay: `${index * 0.1}s` }"
              data-testid="accounts-card"
              :data-account-id="account.id">
              <RouterLink
                :to="{ name: 'settings.accounts.detail', params: { id: account.id } }"
                custom
                v-slot="{ navigate }">
                <div class="card-body p-4 position-relative" @click="navigate">
                  <small
                    class="position-absolute top-0 end-0 p-2 text-primary"
                    v-if="account.is_admin"
                    data-testid="accounts-card-admin"
                    >ADMIN</small
                  >
                  <div class="d-flex">
                    <div>
                      <span class="avatar me-3 rounded">
                        <span class="badge bg-success"></span>{{ getAcronym(account.email) }}
                      </span>
                    </div>
                    <div class="mw-0">
                      <h2 class="fw-bold text-truncate mb-2">
                        {{ account.firstname ?? account.email.substring(0, account.email.indexOf('@')) }}
                        <span class="fw-normal">{{ account.lastname }}</span>
                      </h2>
                      <p class="text-muted text-truncate mb-0" :title="account.email">{{ account.email }}</p>
                    </div>
                  </div>
                </div>
              </RouterLink>
            </div>
          </div>
        </div>
      </template>
      <template #footer="{ page, changePage, startRecordIndex, endRecordIndex, filterCount, pageCount }">
        <FixedFooterSection>
          <p class="m-0">
            <strong>{{ $t('orders.pagination.page') }} {{ page }} {{ $t('common.of') }} {{ pageCount }}</strong>
            <span class="text-muted">
              ({{ startRecordIndex }}-{{ endRecordIndex }} {{ $t('common.of') }} {{ filterCount }}
              {{ $t('settings.accounts.title') }})
            </span>
          </p>
          <div class="d-flex gap-3">
            <button class="btn" :disabled="page <= 1" @click="changePage(page - 1)">
              {{ $t('orders.pagination.prev') }}
            </button>
            <button class="btn" :disabled="page >= pageCount" @click="changePage(page + 1)">
              {{ $t('orders.pagination.next') }}
            </button>
          </div>
        </FixedFooterSection>
      </template>
    </RemoteListView>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import HeaderNavbar from '@/components/header/HeaderNavbar.vue';
import HeaderNavbarSearch from '@/components/header/HeaderNavbarSearch.vue';
import RemoteListView from '@/components/RemoteListView.vue';
import FixedFooterSection from '@/components/generic/FixedFooterSection.vue';
import { getAcronym } from '@/func/string/acronym';
import { IconUserPlus } from '@tabler/icons-vue';
import translator from '@/locale/translator';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { request } from '@/api/client';
import type { CustomerAccount } from '@/types/types';
import { useAutopilotFunctions } from '@/composables/useAutopilotFunctions';
import { pushAndWait } from '@/autopilot/functions/navigate';
import { useAutopilotContext } from '@/composables/useAutopilotContext';
import { useAutopilotRouteReady } from '@/composables/useAutopilotRouteReady';
import { searchFilter } from '@/autopilot/listFilters';
import { buildPjasUri } from '@/func/entityLinks';
import { accountWorkflowGuide } from '@/autopilot/functions/accounts/accountWorkflow';
import type { AutopilotFunctionDefinition } from '@llmor/autopilot-core';
import type { AutopilotFunctionFactory } from '@/autopilot/functions/types';

/* --------------------------------- STATE -------------------------------- */

const searchQuery = ref('');
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

const listBreadcrumbs = computed(() => [
  { label: translator.get('nav.settings'), path: { name: 'settings' } },
  { label: translator.get('settings.accounts.title'), path: { name: 'settings.accounts' } },
]);

const isAdmin = computed(() => {
  return currentUser.value?.is_admin;
});

/* -------------------------------- AUTOPILOT ------------------------------- */
const routeReady = useAutopilotRouteReady();
useAutopilotContext('account_workflow', () => accountWorkflowGuide);

const accountListDescription =
  'The accounts list currently on screen. Each row links to the account detail form. Drive it with account_list_set_filters / account_list_paginate.';

function accountListTransformer(account: CustomerAccount) {
  return {
    id: account.id,
    email: account.email,
    firstname: account.firstname,
    lastname: account.lastname,
    is_admin: account.is_admin,
    is_active: account.is_active,
  };
}

function accountListLink(account: CustomerAccount) {
  return buildPjasUri('account', account.id);
}

const accountListFilters = () => ({ search: searchFilter(searchQuery) });

const gotoCreateAccount: AutopilotFunctionFactory = ({ router: r }) => ({
  name: 'goto_create_account',
  description:
    'Open the empty new-account form. This ONLY navigates and creates nothing. After it returns, use current_account_form_set_fields (firstname, lastname, email, password, is_admin, is_active) then current_account_form_save to persist. Password is required on create. Admin only.',
  parameters: { type: 'object', properties: {} },
  handler: async () => {
    if (!currentUser.value?.is_admin) {
      return { success: false, error: 'Only admins can create accounts.' };
    }
    await pushAndWait(r, { name: 'settings.accounts.detail', params: { id: '+' } });
    return { success: true };
  },
});

const setAccountPassword: AutopilotFunctionDefinition = {
  name: 'set_account_password',
  description: 'Set a new password for an account. Only admins can set passwords for other accounts.',
  sensitive: true,
  parameters: {
    type: 'object',
    properties: {
      account_id: { type: 'number', description: 'The ID of the account to update the password for' },
      password: { type: 'string', description: 'The new password to set' },
    },
    required: ['account_id', 'password'],
  },
  handler: async (rawArgs) => {
    const args = (rawArgs ?? {}) as { account_id?: number; password?: string };
    try {
      if (!args.account_id || !args.password) {
        return { success: false, error: 'account_id and password are required' };
      }
      if (!currentUser.value?.is_admin && currentUser.value?.id !== args.account_id) {
        return { success: false, error: 'Only admins can set passwords for other accounts' };
      }
      await request('PUT', `/v1/account/accounts/${args.account_id}`, {}, { password: args.password });
      return { success: true, message: 'Password updated successfully' };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to set password' };
    }
  },
};

useAutopilotFunctions([gotoCreateAccount, setAccountPassword]);
</script>

<style lang="scss" scoped>
.mw-0 {
  min-width: 0;
}
</style>
